// Adaptive Difficulty Indicator - Visual feedback for question difficulty and AI insights
import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, Target } from 'lucide-react';

const AdaptiveDifficultyIndicator = ({ 
  difficulty = 'medium', 
  isFollowUp = false, 
  isInsightMode = false,
  className = '' 
}) => {
  const getDifficultyConfig = () => {
    switch (difficulty) {
      case 'easy':
        return {
          color: 'from-emerald-400 to-teal-500',
          glow: 'shadow-emerald-200',
          intensity: 0.3
        };
      case 'hard':
        return {
          color: 'from-red-400 to-pink-500',
          glow: 'shadow-red-200',
          intensity: 0.8
        };
      default:
        return {
          color: 'from-blue-400 to-indigo-500',
          glow: 'shadow-blue-200',
          intensity: 0.5
        };
    }
  };

  const config = getDifficultyConfig();

  return (
    <div className={`relative ${className}`}>
      {/* Difficulty Glow Effect */}
      {difficulty === 'hard' && (
        <motion.div
          className={`absolute inset-0 bg-gradient-to-r ${config.color} rounded-2xl blur-sm opacity-20`}
          animate={{
            scale: [1, 1.02, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}

      {/* Follow-up Highlight */}
      {isFollowUp && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ duration: 0.5 }}
        />
      )}

      {/* Insight Mode Indicator */}
      {isInsightMode && (
        <motion.div
          className="absolute -top-2 -right-2 z-10"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2 rounded-full shadow-lg">
            <Brain className="w-4 h-4" />
          </div>
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeOut"
            }}
          />
        </motion.div>
      )}

      {/* Difficulty Badge */}
      <motion.div
        className="absolute top-4 left-4 z-10"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-white/80 backdrop-blur-sm border ${config.glow} shadow-sm`}>
          {difficulty === 'easy' && <Target className="w-3 h-3 text-emerald-600" />}
          {difficulty === 'medium' && <Zap className="w-3 h-3 text-blue-600" />}
          {difficulty === 'hard' && <Brain className="w-3 h-3 text-red-600" />}
          <span className="capitalize text-gray-700">{difficulty}</span>
        </div>
      </motion.div>
    </div>
  );
};

export default AdaptiveDifficultyIndicator;